import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Toast, ToastService } from '../../services/toast.service';
import { FormsModule } from '@angular/forms';
import { ToastType } from '../../../enums/toast-type.enum';
import { MatMenuModule } from '@angular/material/menu';

interface NotificationDayGroup {
    day: string;
    toasts: Toast[];
}

@Component({
    selector: 'app-notifications-history',
    standalone: true,
    imports: [CommonModule, FormsModule, MatMenuModule],
    templateUrl: './notifications-history.component.html',
    styleUrl: './notifications-history.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [DatePipe],
})
export class NotificationsHistoryComponent {
    public toasts: Toast[] = [];
    public dayGroups: NotificationDayGroup[] = [];
    public readonly ToastType = ToastType;

    constructor(
        public readonly toastService: ToastService,
        private readonly datePipe: DatePipe
    ) {
        this.loadNotifications();
        this.toastService.toastAdded$.subscribe(() => {
            this.loadNotifications();
        });
    }

    public get hasUnread(): boolean {
        return this.toasts.some((t) => !t.read);
    }

    public markAsRead(toast: Toast): void {
        if (toast.read) return;
        toast.read = true;
        this.toastService.saveToStorage(this.toasts);
    }

    public onItemClick(toast: Toast): void {
        this.markAsRead(toast);
        if (toast.link) {
            this.toastService.goToLink(toast);
        }
    }

    public markAllAsRead(): void {
        this.toasts = this.toasts.map((t) => ({ ...t, read: true }));
        this.toastService.saveToStorage(this.toasts);
        this.dayGroups = this.buildDayGroups(this.toasts);
    }

    public removeToast(toast: Toast): void {
        this.toasts = this.toasts.filter((t) => t.id !== toast.id);
        this.toastService.saveToStorage(this.toasts);
        this.dayGroups = this.buildDayGroups(this.toasts);
    }

    public clearAll(): void {
        this.toasts = [];
        this.toastService.saveToStorage(this.toasts);
        this.dayGroups = [];
    }

    public loadNotifications(): void {
        this.toasts = this.toastService
            .loadToastsFromStorage()
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        this.dayGroups = this.buildDayGroups(this.toasts);
    }

    private buildDayGroups(toasts: Toast[]): NotificationDayGroup[] {
        const groups: NotificationDayGroup[] = [];
        for (const toast of toasts) {
            const day = this.dayLabel(new Date(toast.timestamp));
            const lastGroup = groups[groups.length - 1];
            if (lastGroup && lastGroup.day === day) {
                lastGroup.toasts.push(toast);
            } else {
                groups.push({ day, toasts: [toast] });
            }
        }
        return groups;
    }

    private dayLabel(date: Date): string {
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (this.isSameDay(date, today)) return 'Today';
        if (this.isSameDay(date, yesterday)) return 'Yesterday';
        return this.datePipe.transform(date, 'MMM d') || '';
    }

    private isSameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }
}
